<script setup lang="ts">
import { computed, inject, ref, watch, type Ref } from 'vue'
import { useRouter } from 'vue-router'
import { SuiDropdown, SuiForm, useToast } from 'vue-fomantic-ui'
import { useIncidentTypeStore } from '@/stores/incident_types'
import { useSyncStore } from '@/stores/sync'
import { ActionType } from '@/actions/Action'
import type { Room } from '@/models/Room'
import type { Container } from '@/models/Container'
import type { IncidentType } from '@/models/IncidentType'
import Title from '@/components/Title.vue'
import Actions from '../components/Actions.vue'

const router = useRouter()
const { toast } = useToast()

const incidentTypeStore = useIncidentTypeStore()

const incidentType = inject('incidentType') as Ref<IncidentType | null>
const incidentMessage = inject('incidentMessage') as Ref<string | null>
const room = inject('room') as Ref<Room | null>
const container = inject('container') as Ref<Container | null>
const picture = inject('picture') as Ref<string | null>

const selectedIncidentType = ref(
  incidentType.value
    ? {
        text: incidentType.value.description,
        value: incidentType,
      }
    : null,
)
watch(selectedIncidentType, (newValue) => {
  incidentType.value = newValue.value
})

const incidentTypes = incidentTypeStore.incidentTypes.map((type) => ({
  text: type.description,
  value: type,
}))

const formCompleted = computed(() => {
  return (
    incidentType.value &&
    (incidentMessage.value || !incidentType.value?.isCommentRequired) &&
    (room.value || container.value)
  )
})

const save = async () => {
  const syncStore = useSyncStore()
  await syncStore.syncAction({
    type: ActionType.NewIncident,
    payload: {
      data: {
        typeCode: incidentType.value?.code,
        containerUuid: container.value?.uuid,
        roomUuid: room.value?.uuid,
        comment: incidentMessage.value || undefined,
      },
      picture: picture.value || undefined,
    },
  })

  toast({
    position: 'centered',
    message: `L'incident a été déclaré.`,
    type: 'success',
  })

  router.push({ name: 'home' })
}
</script>

<template>
  <div class="page report-incident-type">
    <Title>Signaler un incident</Title>
    <SuiForm @submit.prevent="save">
      <div class="field incident-type">
        <label>Sélectionner le type d'incident</label>
        <SuiDropdown
          :options="incidentTypes"
          v-model="selectedIncidentType"
          placeholder="Type d'incident"
          selection
        />
      </div>
      <div
        class="field incident-message"
        :style="{
          visibility: incidentType?.isCommentRequired ? 'visible' : 'hidden',
        }"
      >
        <label>Description du problème</label>
        <textarea
          placeholder="Décrivez le problème que vous rencontrez"
          rows="2"
          v-model="incidentMessage"
        ></textarea>
      </div>
      <Actions />
      <SuiButton class="submit" type="submit" :disabled="!formCompleted">Signaler</SuiButton>
    </SuiForm>
  </div>
</template>

<style scoped lang="scss">
.report-incident-type {
  .field {
    width: 100%;

    label {
      text-align: center;
    }
  }
}

/* @TODO: override Fomantic styles in a cleaner way */
.ui.form {
  padding: 2dvh 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
}
.ui.form .field > label {
  display: block;
  margin: 0 0 0.28571429rem 0;
  font-size: 13px;
  font-weight: 500;
  text-transform: none;
}
.ui.form:not(.inverted) .field > label:not(.button) {
  color: #344c60;
}
.ui.button {
  font-family: 'Libre Franklin', sans-serif;
  padding: 8px 20px 7px 20px;
  background-color: #344c60;
  transition: opacity 0.25s;
  color: white;
  border-radius: 10px;
  font-weight: 600;
  text-transform: uppercase;
  margin: 0;
  height: 40px;
  width: 60%;
  line-height: 22px;
  margin-top: auto;
  margin-bottom: 20px;
}
.ui.input textarea,
.ui.form textarea {
  margin: 0;
  -webkit-appearance: none;
  -webkit-tap-highlight-color: rgba(255, 255, 255, 0);
  padding: 0.78571429em 1em;
  background: #fff;
  border: 1px solid #e0e2ed;
  outline: none;
  color: #344c60;
  border-radius: 2px;
  box-shadow: 0 0 0 0 transparent inset;
  transition:
    color 0.1s ease,
    border-color 0.1s ease;
  font-size: 1em;
  font-family: 'Libre Franklin', sans-serif;
  line-height: 1.2857;
  resize: vertical;
}
</style>
